import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { Policy } from './Policy';
import { Admin } from '../Admin';
import { FollowUpQA } from './FollowUpQA';
import { FollowUpComment } from './FollowUpComment';
import { Utils } from '../Utils';

@supertypeClass({ toClient: true })
export class FollowUp extends Everything {
  static Status = {
    Open: 'Open',
    SupportReady: 'SupportReady',
    Closed: 'Closed',
    Ready: 'Ready',
  };

  static Type = {
    OpsSupport: 'ops',
    CustomerFollowUp: 'customer',
  };

  @property({ getType: () => Policy })
  policy: Policy;

  @property({ getType: () => Admin })
  creator: Admin;

  @property({ getType: () => Admin })
  terminator: Admin;

  @property()
  createdDate: Date;

  @property()
  closedDate: Date;

  @property({ type: Array, getType: () => FollowUpQA })
  questions: Array<FollowUpQA> = [];

  @property({ type: Array, getType: () => FollowUpComment })
  comments: Array<FollowUpComment> = [];

  @property()
  status: string;

  @property()
  type: string;

  @property()
  customerLoginAttemptsRemaning: number = 3;

  @property()
  policyNumber: string;

  @property()
  version: string;

  constructor() {
    super();
  }

  static new(policy, creator, type, followUpQuestion, version?: string) {
    return new FollowUp().init(policy, creator, type, followUpQuestion, version);
  }

  init(policy, creator, type, followUpQuestion, version?: string) {
    this.createdDate = Utils.now();
    this.status = FollowUp.Status.Open;
    this.policy = policy;
    this.policyNumber = policy.policyNumber;
    this.creator = creator;
    this.type = type;
    this.version = version;
    if (followUpQuestion) {
      this.addQuestion(followUpQuestion);
    }
    return this;
  }

  markAsClosed(terminator?: Admin) {
    this.status = FollowUp.Status.Closed;
    this.terminator = terminator;
    this.closedDate = Utils.now();
  }

  markAsOpen() {
    this.status = FollowUp.Status.Open;
  }

  addComment(commentText?, creator?) {
    this.status = creator.role === 'agent' ? FollowUp.Status.SupportReady : FollowUp.Status.Open;
    this.comments.push(FollowUpComment.new(this, commentText, creator));
  }

  addQuestion(qText, followUpMetadataKey?, displayOrder?) {
    const followUpQA = FollowUpQA.new(this, qText);
    followUpQA.linkToQuestionRef(followUpMetadataKey);
    followUpQA.displayOrder = displayOrder;
    followUpQA.section = followUpMetadataKey;
    this.questions.push(followUpQA);
  }

  addRefQuestion(followUpMetadataKey, displayOrder, section) {
    const followUpQA = FollowUpQA.new(this);
    followUpQA.displayOrder = displayOrder;
    followUpQA.linkToQuestionRef(followUpMetadataKey, section);
    this.questions.push(followUpQA);
  }

  getSortedComments() {
    if (this.comments.length > 0) {
      return this.comments.sort((c1, c2) => {
        return c1.createdDate.valueOf() - c2.createdDate.valueOf();
      });
    } else {
      return [];
    }
  }

  static hasOpenFollowUps(followUps: Array<FollowUp>): boolean {
    if (followUps) {
      return followUps.some((f) => f.status === FollowUp.Status.Open);
    }
    return false;
  }
}
